<template lang="pug">
.mt-3(v-if="loggingIn")
  div {{ $t('components.login_bridge.tpl.authenticating') }}...
.mt-3(
  v-else
)
  .card
    .card-header.font-weight-bold {{ $t("components.restore_password.tpl.card_header") }}

    .card-body
      .row.w-100
        span.col-xs.col-md-2.font-weight-bold {{ $t("components.restore_password.tpl.name") }}
        mark.col-sx.col-md-10 {{ userName }}
      .mt-1.row.w-100
        span.col-xs.col-md-2.font-weight-bold
          | {{ $t("components.restore_password.tpl.user_name") }}
        mark.col-xs.col-md-10 {{ userUsername }}

      p.card-text.mt-3(
        v-if="!success"
      ) {{ $t("components.restore_password.tpl.card_text") }}
        password.mt-3(
          :placeholder="$t('components.restore_password.tpl.first_input_placeholder')"
          v-model="newPassword1"
          defaultClass="ant-input"
          :showPassword="false"
          :badge="false"
          :secureLength="6"
          :showStrengthMeter="false"
          ref="password1"
        )
        password.mt-2(
          :placeholder="$t('components.restore_password.tpl.second_input_placeholder')"
          v-model="newPassword2"
          defaultClass="ant-input"
          :showPassword="false"
          :badge="false"
          :secureLength="6"
          ref="password2"
        )

      a-alert.my-3(
        v-if="success"
        :message="$t('components.restore_password.tpl.alert_message')"
        :description="$t('components.restore_password.tpl.alert_success_message')"
        type="success"
      )

      .d-flex.justify-content-end
        button.mr-2.btn.btn-primary(
          v-if="!success"
          :disabled="!newPassword1 || !newPassword2 || !newPasswordIsValid"
          type="button"
          @click="handleRestorePassword"
        ) {{ $t("components.restore_password.tpl.save_button") }}

        .btn.btn-outline-primary(
          v-if="redirectUrl"
          type="button"
          @click="handleGoToRedirectUrl"
        ) {{ $t("components.restore_password.tpl.redirect_button") }}

      a-alert.mt-4(
        v-if="validationError"
        :message="$t('components.restore_password.tpl.alert_message')"
        :description="$t('components.restore_password.tpl.alert_validation_error_message')"
        type="warning"
      )

      a-alert.mt-4(
        v-if="errorRestoringUserPassword"
        :message="$t('components.restore_password.tpl.alert_message')"
        :description="$t('components.restore_password.tpl.error_updating_user_pwd_message')"
        type="error"
      )

</template>

<script>
import Password from 'vue-password-strength-meter';
import { mapActions } from 'vuex';
import { URLS } from '@/util';
import { restoreUserPasswordWithRefererAuth } from '@/api';

export default {
  components: {
    Password,
  },
  props: {
    userId: {
      type: String,
      default: '',
    },
    userName: {
      type: String,
      default: '',
    },
    userUsername: {
      type: String,
      default: '',
    },
    userMail: {
      type: String,
      default: '',
    },
    instance: {
      type: String,
      default: '',
    },
    redirectUrl: {
      type: String,
      default: null,
    },
    validUntil: {
      type: String,
      default: null,
    },
    autoLogin: {
      type: String,
      default: '0',
    },
  },
  data() {
    return {
      newPassword1: null,
      newPassword2: null,
      success: false,
      validationError: false,
      errorRestoringUserPassword: false,
      loggingIn: false,
    };
  },
  mounted() {
    if (new Date() > new Date(Number(this.validUntil))) {
      this.handleGoToRedirectUrl();
    }

    const bindEnterToPasswordInput = input => input.$el.childNodes[0].childNodes[0].addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        this.handleRestorePassword();
      }
    });

    bindEnterToPasswordInput(this.$refs.password1);
    bindEnterToPasswordInput(this.$refs.password2);
  },
  methods: {
    ...mapActions('login', [
      'doLogin',
    ]),
    newPasswordIsValid() {
      return this.newPassword1 === this.newPassword2
        && this.newPassword1.length >= 6
        && this.newPassword2.length >= 6;
    },
    async handleRestorePassword() {
      if (!this.newPasswordIsValid()) {
        this.validationError = true;
        return;
      }

      try {
        await restoreUserPasswordWithRefererAuth({
          referer: window.location.href.split('?')[0],
          instance: this.instance,
          userEntity: {
            id: this.userId,
            username: this.userUsername,
            password: this.newPassword1,
            audit: {
              userId: this.userId,
            },
          },
        });

        if (this.autoLogin === '1') {
          this.loggingIn = true;

          await this.doLogin({
            instance: this.instance,
            username: this.userUsername,
            password: this.newPassword2,
          });

          this.$router.replace({
            name: 'projects',
            params: {
              instance: this.instance,
            },
          });
        }

        this.validationError = false;
        this.success = true;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          const {
            resourceUrl,
          } = error.response.data;

          window.location = resourceUrl;
        } else {
          console.log(error);
          this.errorRestoringUserPassword = true;
        }
      }
    },
    handleGoToRedirectUrl() {
      if (!this.redirectUrl) {
        window.location = URLS.defaultRedirect;
      }

      window.location.replace(decodeURIComponent(atob(this.redirectUrl)));
    },
  },
};
</script>

<style scoped>
.Password {
  max-width: none;
}
</style>
